import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Drawer, Toolbar, Box, List, ListItem, ListItemIcon,
  ListItemText, Collapse, useTheme, useMediaQuery, IconButton
} from '@mui/material';
import {
  ExpandLess, ExpandMore, Dashboard, ChevronLeft
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { buildMenuTree } from '../utils/menuUtils';
import { getMenuIcon, getSubMenuIcon } from '../utils/menuIcons';

const drawerWidth = 260;

const LeftDrawer = ({ activeController, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { routes } = useSelector((state) => state.auth);
  const [menuTree, setMenuTree] = useState([]);
  const [openMenus, setOpenMenus] = useState({});

  
  useEffect(() => {
    if (routes && Array.isArray(routes) && routes.length > 0) {
      const tree = buildMenuTree(routes);
      setMenuTree(tree);

      const initialOpenMenus = {};
      tree.forEach((item) => {
        if (item.children.some(child => child.controller === activeController)) {
          initialOpenMenus[item.pageLabel] = true;
        }
      });
      setOpenMenus(initialOpenMenus);
    }
  }, [routes, activeController]);

  const toggleSubMenu = (title) => {
    setOpenMenus((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const handleNavigation = (url) => {
    if (url) {
      navigate(url);
      if (isMobile && onMobileClose) {
        onMobileClose();
      }
    }
  };

  const drawerContent = (
    <>
      <Toolbar sx={{
        minHeight: 64,
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: (theme) => theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.04)'
          : 'rgba(0, 0, 0, 0.02)',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        mb: 1
      }}>
        {isMobile && (
          <IconButton
            onClick={onMobileClose}
            sx={{
              color: 'text.primary',
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.08)'
              }
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}
      </Toolbar>
      <Box sx={{ overflow: 'auto', px: 1.5 }}>
        <List dense sx={{ py: 1 }}>
          {/* Static Dashboard Item */}
          <ListItem
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              mb: 0.5,
              mx: 0.5,
              py: 1,
              ...(activeController === '/dashboard' && {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }),
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
            button
            onClick={() => handleNavigation('/dashboard')}
            selected={activeController === '/dashboard'}
          >
            <ListItemIcon sx={{
              minWidth: 40,
              color: activeController === '/dashboard' ? 'inherit' : 'text.secondary'
            }}>
              <Dashboard fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary='Dashboard'
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: activeController === '/dashboard' ? 600 : 500
              }}
            />
          </ListItem>

          {/* Dynamic Menu Tree */}
          {menuTree.map((item) => (
            <React.Fragment key={item.pageId}>
              {/* Parent menu item */}
              <ListItem
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 0.5,
                  py: 1,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                button
                onClick={() =>
                  item.children.length > 0
                    ? toggleSubMenu(item.pageLabel)
                    : handleNavigation(item.controller)
                }
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                  {React.createElement(getMenuIcon(item.pageLabel, item.controller), { fontSize: 'small' })}
                </ListItemIcon>
                <ListItemText
                  primary={item.pageLabel}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                />
                {item.children.length > 0 &&
                  (openMenus[item.pageLabel] ? (
                    <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} />
                  ) : (
                    <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />
                  ))}
              </ListItem>

              {/* Sub-menu items */}
              {item.children.length > 0 && (
                <Collapse in={openMenus[item.pageLabel]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {item.children.map((subItem) => (
                      <ListItem
                        button
                        key={subItem.pageId}
                        sx={{
                          pl: 5,
                          pr: 2,
                          py: 0.75,
                          cursor: 'pointer',
                          borderRadius: 1.5,
                          mx: 1,
                          mb: 0.25,
                          ...(activeController === subItem.controller && {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          }),
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                        onClick={() => handleNavigation(subItem.controller)}
                        selected={activeController === subItem.controller}
                      >
                        <ListItemIcon sx={{
                          minWidth: 36,
                          color: activeController === subItem.controller ? 'inherit' : 'text.secondary'
                        }}>
                          {React.createElement(getSubMenuIcon(subItem.pageLabel, subItem.controller), {
                            fontSize: 'small'
                          })}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.pageLabel}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: activeController === subItem.controller ? 600 : 400,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: 'background.paper',
              borderRight: 'none',
              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop Drawer */
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'background.paper',
              borderRight: 'none',
              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default LeftDrawer;
